import { useState, useEffect } from "react";
import { MessageSquare, Send, Trash2, ChevronDown, ChevronUp, User, Shield } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function DiscussionForum({ courseId }) {
  const { user } = useAuth();
  const [discussions, setDiscussions] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [replyText, setReplyText] = useState({});
  const [replyLoading, setReplyLoading] = useState("");

  const fetchDiscussions = async () => {
    try {
      const { data } = await api.get(`/discussions/course/${courseId}`);
      setDiscussions(data || []);
    } catch (err) {
      console.error("Failed to load discussions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscussions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setPosting(true);
    try {
      await api.post("/discussions", { course_id: courseId, message: newMessage.trim() });
      setNewMessage("");
      fetchDiscussions();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to post");
    } finally {
      setPosting(false);
    }
  };

  const handleReply = async (discussionId) => {
    const text = replyText[discussionId]?.trim();
    if (!text) return;
    setReplyLoading(discussionId);
    try {
      await api.post(`/discussions/${discussionId}/reply`, { message: text });
      setReplyText((prev) => ({ ...prev, [discussionId]: "" }));
      fetchDiscussions();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to reply");
    } finally {
      setReplyLoading("");
    }
  };

  const handleDelete = async (discussionId) => {
    if (!window.confirm("Delete this discussion?")) return;
    try {
      await api.delete(`/discussions/${discussionId}`);
      fetchDiscussions();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete");
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  if (loading) {
    return <div className="py-6 text-center text-xs text-slate-400">Loading discussions...</div>;
  }

  return (
    <div className="mt-6" data-testid="discussion-forum">
      <h2 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        <MessageSquare size={18} className="text-[#0284C7]" /> Discussion Forum
        <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 font-semibold">{discussions.length}</span>
      </h2>

      {/* Post new question */}
      <form onSubmit={handlePost} className="flex gap-2 mb-4" data-testid="discussion-post-form">
        <input
          data-testid="discussion-input"
          type="text"
          placeholder="Ask a question or share feedback..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          className="flex-1 bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#0284C7]"
          maxLength={500}
        />
        <button
          data-testid="discussion-post-btn"
          type="submit"
          disabled={posting || !newMessage.trim()}
          className="bg-[#0284C7] text-white px-3 py-2.5 disabled:opacity-50 flex items-center"
        >
          <Send size={16} />
        </button>
      </form>

      {/* Discussions list */}
      {discussions.length === 0 ? (
        <div className="text-center py-8" data-testid="discussion-empty">
          <MessageSquare size={32} className="mx-auto text-slate-200 mb-2" />
          <p className="text-xs text-slate-400">No discussions yet. Be the first to ask!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {discussions.map((d) => (
            <div key={d.id} data-testid={`discussion-${d.id}`} className="bg-white border border-slate-100 p-3">
              {/* Main message */}
              <div className="flex items-start gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                  d.user_role === "admin" ? "bg-amber-50" : "bg-slate-100"
                }`}>
                  {d.user_role === "admin" ? <Shield size={12} className="text-amber-600" /> : <User size={12} className="text-slate-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-900">{d.user_name}</span>
                    {d.user_role === "admin" && (
                      <span className="text-[9px] font-bold bg-amber-50 text-amber-700 px-1.5 py-0.5">ADMIN</span>
                    )}
                    <span className="text-[10px] text-slate-300">{formatDate(d.created_at)}</span>
                  </div>
                  <p className="text-sm text-slate-700 mt-0.5 break-words">{d.message}</p>
                </div>
                {(user?.role === "admin" || user?.id === d.user_id) && (
                  <button data-testid={`delete-discussion-${d.id}`} onClick={() => handleDelete(d.id)} className="text-slate-300 hover:text-red-400 flex-shrink-0">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>

              {/* Replies toggle */}
              {(d.replies?.length > 0 || true) && (
                <button
                  data-testid={`toggle-replies-${d.id}`}
                  onClick={() => setExpandedId(expandedId === d.id ? null : d.id)}
                  className="text-[10px] font-bold text-[#0284C7] mt-2 flex items-center gap-1"
                >
                  {expandedId === d.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  {d.replies?.length || 0} {d.replies?.length === 1 ? "reply" : "replies"}
                </button>
              )}

              {/* Expanded replies */}
              {expandedId === d.id && (
                <div className="mt-2 ml-9 space-y-2">
                  {d.replies?.map((r) => (
                    <div key={r.id} data-testid={`reply-${r.id}`} className="bg-slate-50 p-2 border-l-2 border-[#0284C7]/20">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-700">{r.user_name}</span>
                        {r.user_role === "admin" && (
                          <span className="text-[8px] font-bold bg-amber-50 text-amber-700 px-1 py-0.5">ADMIN</span>
                        )}
                        <span className="text-[9px] text-slate-300">{formatDate(r.created_at)}</span>
                      </div>
                      <p className="text-xs text-slate-600 mt-0.5 break-words">{r.message}</p>
                    </div>
                  ))}

                  {/* Reply input */}
                  <div className="flex gap-1.5 mt-1" data-testid={`reply-form-${d.id}`}>
                    <input
                      data-testid={`reply-input-${d.id}`}
                      type="text"
                      placeholder="Write a reply..."
                      value={replyText[d.id] || ""}
                      onChange={(e) => setReplyText((prev) => ({ ...prev, [d.id]: e.target.value }))}
                      className="flex-1 bg-white border border-slate-200 px-2 py-1.5 text-xs focus:outline-none focus:border-[#0284C7]"
                      maxLength={300}
                    />
                    <button
                      data-testid={`reply-send-${d.id}`}
                      onClick={() => handleReply(d.id)}
                      disabled={replyLoading === d.id || !replyText[d.id]?.trim()}
                      className="bg-[#0284C7] text-white px-2 py-1.5 disabled:opacity-50"
                    >
                      <Send size={12} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
